import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { map, takeUntil } from 'rxjs/operators';
import { AllianceLockboxAssociation } from '../../models/entities/alliance/alliance-lockbox-association';
import { AllianceBankAccount } from '../../models/entities/alliance/alliance-bank-account';
import { AllianceLockboxBatch } from '../../models/entities/alliance/alliance-lockbox-batch';
import { DataState } from '../data-state';
import { AllianceTransaction } from '../../models/entities/alliance/alliance-transaction';
import { Moment } from 'moment';

export interface LockboxDocumentImageResponse
{
    frontImage: string; // base64 encoded
    backImage: string;
}

@Injectable({
    providedIn: 'root',
})
export class AllianceManagementCompanyService
{
    public lockboxAssociationsState = new DataState<AllianceLockboxAssociation>('associationId');
    public bankAccountsState = new DataState<AllianceBankAccount>('accountNumber');
    public lockboxBatchesState = new DataState<AllianceLockboxBatch>('batch');
    public transactionsState = new DataState<AllianceTransaction>('transactionID');

    constructor(
            private _http: ApplicationHttpClient,
    ) {
    }

    getLockboxAssociations(allianceCredentialId: number): Observable<AllianceLockboxAssociation[]> {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/lockbox-associations',
        );

        this.lockboxAssociationsState.loading();
        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
        })).pipe(takeUntil(this.lockboxAssociationsState.$interrupt)).pipe(map((response: Object[]) => {
            const allianceCredentials = response.map(element => new AllianceLockboxAssociation(element));
            this.lockboxAssociationsState.set(allianceCredentials);
            return allianceCredentials;
        }));
    }

    getBankAccounts(allianceCredentialId: number): Observable<AllianceBankAccount[]> {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/bank-accounts',
        );

        this.bankAccountsState.loading();
        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
        })).pipe(takeUntil(this.bankAccountsState.$interrupt)).pipe(map((response: Object[]) => {
            const bankAccounts = response.map(element => new AllianceBankAccount(element));
            this.bankAccountsState.set(bankAccounts);
            return bankAccounts;
        }));
    }

    getLockboxBatches(
            allianceCredentialId: number,
            associationId,
            from: Moment,
            to: Moment,
    ): Observable<AllianceLockboxBatch[]> {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/lockbox-batches',
        );

        this.lockboxBatchesState.loading();
        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
            associationId: associationId,
            fromDate: from.format('YYYY-MM-DD'),
            toDate: to.format('YYYY-MM-DD'),
        })).pipe(takeUntil(this.lockboxBatchesState.$interrupt)).pipe(map((response: Object[]) => {
            const lockboxBatches = response.map(element => new AllianceLockboxBatch(element));
            this.lockboxBatchesState.set(lockboxBatches);
            return lockboxBatches;
        }));
    }

    overrideDocumentPayor(
            associationId: string,
            documentId: string,
            unitId: number,
    ) {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/lockbox-batches/override',
        );

        return this._http.post(url, JSON.stringify({
            associationId: associationId,
            documentId: documentId,
            unitId: unitId,
        }));
    }

    getLockboxDocumentImage(
            allianceCredentialId: number,
            associationId,
            memberId,
            documentId,
            date,
    ) {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/lockbox-batches/document',
        );

        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
            associationId: associationId,
            memberId: memberId,
            documentId: documentId,
            processDate: date,
        }));
    }

    importLockboxBatch(
            allianceCredentialId: number,
            associationId,
            batchDate: Moment,
            amount: number,
            itemCount: number,
            reprocess = false,
    ): Observable<void> {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/lockbox-batches/import',
        );

        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
            associationId: associationId,
            batchDate: batchDate.format('YYYY-MM-DD'),
            amount: amount,
            itemCount: itemCount,
            reprocess: reprocess,
        }));
    }

    getTransactions(
            allianceCredentialId: number,
            accountNumber: string,
            from: Moment,
            to: Moment,
    ): Observable<AllianceTransaction[]> {
        const url: string = Routes.MakeLegFiCoreUrl(
                '/admin/alliance/transactions',
        );

        this.transactionsState.loading();
        return this._http.post(url, JSON.stringify({
            credentialId: allianceCredentialId,
            accountNumber: accountNumber,
            fromDate: from.format('YYYY-MM-DD'),
            toDate: to.format('YYYY-MM-DD'),
        })).pipe(takeUntil(this.transactionsState.$interrupt)).pipe(map((response: Object[]) => {
            const transactions = response.map(element => new AllianceTransaction(element));
            this.transactionsState.set(transactions);
            return transactions;
        }));
    }
}
